import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import 'wicg-inert';
import {ModalData} from './modal-trigger';

type Options = Partial<ModalOptions>;

export interface ModalElement extends HTMLElement {
  modal?: Modal;
}

export interface ModalOptions {
  activeClass: string;
  class: string;
  closeSelector: string;
  contentSelector: string;
  errorClass: string;
  inertSelector: string;
  isStatic: boolean;
  loadedClass: string;
  lockTargetSelector: string;
  modalSelector: string;
  overlaySelector: string;
  stylesLoadedClass: string;
}

export interface RequestOptions {
  containsExternalStylesheet?: boolean;
  url: string;
  targetElementSelector: string;
}

interface InertHTMLElement extends HTMLElement {
  inert: boolean;
}

export class Modal {
  static defaults = {
    activeClass: 'is-active',
    class: '',
    closeSelector: '[data-modal-close]',
    contentSelector: '[data-modal-content]',
    errorClass: 'error',
    inertSelector: '[data-modal-inert]',
    isStatic: false,
    loadedClass: 'content-loaded',
    lockTargetSelector: '[data-modal-lock-target]',
    modalSelector: '[data-modal]',
    overlaySelector: '[data-modal-overlay]',
    stylesLoadedClass: 'external-styles-loaded',
  };

  element: ModalElement;
  options: ModalOptions;
  cachedRequest: RequestOptions;
  currentModalData?: ModalData;
  currentTrigger?: HTMLElement;
  closeTrigger: HTMLElement;
  content: string;
  contentElement: HTMLElement;
  inertElement: InertHTMLElement;
  lockTarget: HTMLElement;
  modal: HTMLElement;
  overlay: HTMLElement;

  constructor(element: ModalElement, options?: Options) {
    if (!element || element.modal) {
      return;
    }

    element.modal = this;
    this.element = element;

    this.options = {
      ...Modal.defaults,
      ...this.element.dataset,
      ...options,
    };

    const closeTrigger = this.element.querySelector(
      this.options.closeSelector,
    ) as HTMLElement;
    const contentElement = this.element.querySelector(
      this.options.contentSelector,
    ) as HTMLElement;
    const inertElement = document.querySelector(
      this.options.inertSelector,
    ) as HTMLElement;
    const lockTarget = this.element.querySelector(
      this.options.lockTargetSelector,
    ) as HTMLElement;
    const overlay = this.element.querySelector(
      this.options.overlaySelector,
    ) as HTMLElement;

    if (!closeTrigger || !lockTarget || !overlay) {
      return;
    }

    this.closeTrigger = closeTrigger;
    this.contentElement = contentElement;
    this.inertElement = inertElement;
    this.lockTarget = lockTarget;
    this.overlay = overlay;

    this.bind();
  }

  bind() {
    this.closeTrigger.addEventListener('click', this.handleCloseClick);
    this.overlay.addEventListener('click', this.handleOverlayClick);
    this.lockTarget.addEventListener('keydown', this.handleKeyDown);
    document.documentElement.addEventListener(
      'modal-open',
      this.handleModalOpen,
    );
  }

  handleCloseClick = () => {
    this.close();
  };

  handleOverlayClick = (event: Event) => {
    if (event.target !== this.overlay) {
      return;
    }

    this.close();
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === 27) {
      event.preventDefault();
      this.close();
    }
  };

  enableBodyFocus = () => {
    this.inertElement.inert = false;
  };

  disableBodyFocus = () => {
    this.inertElement.inert = true;
  };

  handleModalOpen = (event: Event) => {
    if (this.isActive() || !event) {
      return;
    }

    const {detail: modalData = {}, target} = event as CustomEvent;

    if (modalData.targetModal === this.options.modalSelector) {
      this.currentTrigger = target as HTMLElement;
      this.open(modalData);
    }
  };

  open = (modalData: ModalData) => {
    this.element.classList.add(this.options.activeClass);
    disableBodyScroll(this.lockTarget, {
      allowTouchMove: el =>
        el.getAttribute('data-body-scroll-lock-ignore') !== null,
    });
    this.disableBodyFocus();
    this.lockTarget.focus();
    this.currentModalData = modalData;

    if (modalData.class) {
      this.element.classList.add(modalData.class);
    }

    if (modalData.onOpen) {
      modalData.onOpen(this.element);
    }

    if (!this.options.isStatic) {
      if (
        modalData.shouldCache &&
        this.cachedRequest === modalData.requestOptions
      ) {
        return;
      }

      if (modalData.requestOptions && modalData.requestOptions.url) {
        this.loadContent(modalData);
      }
    }
  };

  loadContent(data: ModalData) {
    this.clearContent();
    this.element.classList.remove(this.options.loadedClass);
    this.element.classList.remove(this.options.errorClass);
    this.element.classList.remove(this.options.stylesLoadedClass);

    fetch(data.requestOptions.url)
      .then(response => {
        return response.text();
      })
      .then(contentString => {
        const parser = new DOMParser();
        const contentHTML = parser.parseFromString(contentString, 'text/html');
        const content = contentHTML.querySelector(
          data.requestOptions.targetElementSelector,
        ) as HTMLElement;

        if (!content) {
          this.showError();
          return;
        }

        this.setContent(content);

        if (data.requestOptions.containsExternalStylesheet) {
          // The requested content is expected to contain an external stylesheet
          // First check if the stylesheet exists
          const stylesheet = content.querySelector(
            'link[rel="stylesheet"]',
          ) as HTMLLinkElement;

          // If we don't find the stylesheet, show an error instead of unstyled content
          if (!stylesheet) {
            this.showError();
            return;
          }

          // When the stylesheet loads, set a class on the modal root
          stylesheet.onload = () => {
            this.element.classList.add(this.options.stylesLoadedClass);
            this.setLoaded(data);
          };

          stylesheet.onerror = () => {
            this.showError();
          };
        } else {
          // If there's no external stylesheet, set the loaded state as usual
          this.setLoaded(data);
        }
      })
      .catch(() => {
        this.showError();
      });
  }

  setLoaded(data: ModalData) {
    this.cachedRequest = data.requestOptions;
    this.element.classList.add(this.options.loadedClass);

    if (data.onLoad) {
      data.onLoad(this.element);
    }
  }

  clearContent() {
    this.contentElement.innerHTML = '';
  }

  setContent(content: HTMLElement) {
    this.contentElement.appendChild(content);
  }

  close = () => {
    this.element.classList.remove(this.options.activeClass);
    enableBodyScroll(this.lockTarget);
    this.enableBodyFocus();

    if (this.currentTrigger) {
      this.currentTrigger.focus();
    }

    if (this.currentModalData && this.currentModalData.class) {
      this.element.classList.remove(this.currentModalData.class);
    }

    this.currentModalData = undefined;
    this.currentTrigger = undefined;
  };

  isActive(): boolean {
    return this.element.classList.contains(this.options.activeClass);
  }

  showError = () => {
    this.element.classList.add(this.options.errorClass);
  };

  unbind() {
    this.closeTrigger.removeEventListener('click', this.handleCloseClick);
    this.overlay.removeEventListener('click', this.handleOverlayClick);
    this.lockTarget.removeEventListener('keydown', this.handleKeyDown);
    document.documentElement.removeEventListener(
      'modal-open',
      this.handleModalOpen,
    );
  }

  destroy() {
    this.unbind();
    this.element.modal = undefined;
    this.close();
  }
}
